import React, { useEffect, useState, useCallback } from "react";
import { Loader2, Plus, Phone, Mail, MapPin, IndianRupee, Calendar, User as UserIcon, ShieldCheck, FlameKindling, X, RefreshCw, Search } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const STAGES = [
  { key: "new",                    label: "New",                   color: "bg-blue-50 border-blue-300 text-blue-800" },
  { key: "contacted",              label: "Contacted",             color: "bg-sky-50 border-sky-300 text-sky-800" },
  { key: "requirement_collected",  label: "Req. Collected",        color: "bg-cyan-50 border-cyan-300 text-cyan-800" },
  { key: "quotation_sent",         label: "Quotation Sent",        color: "bg-violet-50 border-violet-300 text-violet-800" },
  { key: "follow_up",              label: "Follow-up",             color: "bg-amber-50 border-amber-300 text-amber-800" },
  { key: "negotiation",            label: "Negotiation",           color: "bg-orange-50 border-orange-300 text-orange-800" },
  { key: "converted",              label: "Converted",             color: "bg-emerald-50 border-emerald-300 text-emerald-800" },
  { key: "lost",                   label: "Lost",                  color: "bg-rose-50 border-rose-300 text-rose-800" },
];

const CATEGORY_BADGE = {
  hot:  "bg-red-600 text-white",
  warm: "bg-amber-500 text-white",
  cold: "bg-slate-400 text-white",
};

export default function SalesLeads() {
  const [board, setBoard] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [dragId, setDragId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, s] = await Promise.all([
        api.get("/leads/board"),
        api.get("/leads/stats"),
      ]);
      setBoard(b.data);
      setStats(s.data);
    } catch (e) {
      toast.error("Failed to load leads");
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const onDragStart = (e, leadId) => { setDragId(leadId); e.dataTransfer.effectAllowed = "move"; };
  const onDragOver = (e) => {
    e.preventDefault();
    try { e.dataTransfer.dropEffect = "move"; } catch { /* noop */ }
  };
  const onDrop = async (e, targetStage) => {
    e.preventDefault();
    if (!dragId) return;
    const id = dragId;
    setDragId(null);
    try {
      await api.post(`/leads/${id}/move`, { pipeline_stage: targetStage });
      toast.success("Moved");
      load();
    } catch { toast.error("Move failed"); }
  };

  const filteredCard = (l) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (l.name || "").toLowerCase().includes(s) ||
           (l.phone || "").includes(s) ||
           (l.lead_id || "").toLowerCase().includes(s) ||
           (l.city || "").toLowerCase().includes(s);
  };

  return (
    <div className="space-y-4" data-testid="sales-leads-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl font-bold text-[#1a3c5e]">Sales Pipeline</h2>
          <p className="text-xs text-gray-500 mt-0.5">Drag cards between stages · Auto-created leads from WhatsApp appear in &quot;New&quot;</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-gray-400" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, phone, city..." data-testid="leads-search" className="pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#F7941D]" />
          </div>
          <button onClick={load} className="p-2 bg-white border border-gray-300 rounded-xl hover:bg-gray-50" title="Refresh"><RefreshCw className="w-4 h-4" /></button>
          <button onClick={() => setCreating(true)} data-testid="new-lead-btn" className="flex items-center gap-1.5 px-3 py-2 bg-[#F7941D] hover:bg-[#e0861a] text-white text-sm font-bold rounded-xl shadow-sm">
            <Plus className="w-4 h-4" /> New Lead
          </button>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <StatPill label="Total" value={stats.total} color="text-[#1a3c5e]" testId="stat-total" />
          <StatPill label="New Today" value={stats.new_today} color="text-blue-600" testId="stat-new-today" />
          <StatPill label="🔥 Hot" value={stats.by_category.hot} color="text-red-600" testId="stat-hot" />
          <StatPill label="Warm" value={stats.by_category.warm} color="text-amber-600" testId="stat-warm" />
          <StatPill label="Cold" value={stats.by_category.cold} color="text-slate-500" testId="stat-cold" />
          <StatPill label="Follow-ups Due" value={stats.follow_ups_due} color={stats.follow_ups_due > 0 ? "text-orange-600" : "text-emerald-600"} testId="stat-fu-due" />
        </div>
      )}

      {loading ? (
        <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin text-[#F7941D] mx-auto" /></div>
      ) : !board ? (
        <div className="py-12 text-center text-gray-400 text-sm">No data</div>
      ) : (
        <div className="overflow-x-auto pb-3">
          <div className="flex gap-3 min-w-max" data-testid="kanban-board">
            {STAGES.map(stage => {
              const list = (board.board[stage.key] || []).filter(filteredCard);
              return (
                <div
                  key={stage.key}
                  onDragOver={onDragOver}
                  onDrop={(e) => onDrop(e, stage.key)}
                  data-testid={`col-${stage.key}`}
                  className={`w-72 flex-shrink-0 rounded-xl border-2 ${stage.color.split(" ").filter(c => c.startsWith("border")).join(" ")} bg-white/60`}
                >
                  <div className={`px-3 py-2 rounded-t-xl font-bold text-xs uppercase tracking-wider flex justify-between items-center ${stage.color}`}>
                    <span>{stage.label}</span>
                    <span className="bg-white/60 px-1.5 py-0.5 rounded text-[10px]">{list.length}</span>
                  </div>
                  <div className="p-2 space-y-2 min-h-[200px] max-h-[65vh] overflow-y-auto">
                    {list.length === 0 && <div className="text-[11px] text-gray-400 italic text-center py-6" data-testid={`empty-${stage.key}`}>Drop leads here</div>}
                    {list.map(lead => (
                      <LeadCard key={lead.id} lead={lead} onDragStart={(e) => onDragStart(e, lead.id)} onClick={() => setEditing(lead)} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {creating && <LeadModal onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
      {editing && <LeadDrawer lead={editing} onClose={() => setEditing(null)} onChanged={() => load()} />}
    </div>
  );
}

function StatPill({ label, value, color, testId }) {
  return (
    <div className="rounded-xl border border-[#1a3c5e]/10 bg-white p-3" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-gray-500">{label}</div>
      <div className={`font-display font-extrabold text-2xl ${color}`}>{value}</div>
    </div>
  );
}

function LeadCard({ lead, onDragStart, onClick }) {
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onClick={onClick}
      data-testid={`lead-card-${lead.id}`}
      className="bg-white border border-gray-200 rounded-lg p-2.5 cursor-pointer hover:shadow-md transition-shadow"
    >
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="font-semibold text-[#1a3c5e] text-sm truncate flex-1">{lead.name}</div>
        <span className={`text-[9px] uppercase font-bold px-1.5 py-0.5 rounded ${CATEGORY_BADGE[lead.category] || CATEGORY_BADGE.cold}`}>{lead.category}</span>
      </div>
      <div className="text-[10px] text-gray-500 font-mono">{lead.lead_id}</div>
      <div className="text-xs text-gray-600 mt-1 space-y-0.5">
        {lead.phone && <div className="flex items-center gap-1"><Phone className="w-3 h-3" />{lead.phone}</div>}
        {lead.city && <div className="flex items-center gap-1"><MapPin className="w-3 h-3" />{lead.city}</div>}
        {lead.budget && <div className="flex items-center gap-1"><IndianRupee className="w-3 h-3" />{lead.budget}</div>}
      </div>
      <div className="flex items-center justify-between mt-2 text-[10px] text-gray-500">
        <span className="bg-[#FEF3E2] text-[#F7941D] px-1.5 py-0.5 rounded font-bold">Score {lead.score}</span>
        <span>{lead.source}</span>
      </div>
    </div>
  );
}

function LeadDrawer({ lead, onClose, onChanged }) {
  const [full, setFull] = useState(lead);
  const [note, setNote] = useState("");
  useEffect(() => {
    api.get(`/leads/${lead.id}`).then(r => setFull(r.data)).catch(() => {});
  }, [lead.id]);

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      const r = await api.post(`/leads/${lead.id}/note`, { note });
      setFull({ ...r.data, timeline: full.timeline });
      setNote("");
      toast.success("Note added");
      const refreshed = await api.get(`/leads/${lead.id}`);
      setFull(refreshed.data);
      onChanged();
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-md h-full overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="lead-drawer">
        <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <div className="font-display font-bold text-[#1a3c5e]">{full.name}</div>
            <div className="text-[10px] text-gray-500 font-mono">{full.lead_id}</div>
          </div>
          <button onClick={onClose}><X className="w-5 h-5 text-gray-400" /></button>
        </div>
        <div className="p-5 space-y-3 text-sm">
          <Field icon={Phone} label="Phone" value={full.phone} />
          <Field icon={Mail} label="Email" value={full.email} />
          <Field icon={MapPin} label="City" value={full.city} />
          <Field icon={IndianRupee} label="Budget" value={full.budget} />
          <Field icon={UserIcon} label="Plot Size" value={full.plot_size} />
          <Field icon={ShieldCheck} label="Source" value={full.source} />
          <Field icon={FlameKindling} label={`Score / Category`} value={`${full.score} · ${full.category}`} />
          {full.requirement && <div className="bg-gray-50 rounded-lg p-2 text-xs"><b>Requirement:</b> {full.requirement}</div>}
          {full.notes && <div className="bg-amber-50 rounded-lg p-2 text-xs"><b>Notes:</b> {full.notes}</div>}

          <div className="pt-3 border-t">
            <div className="text-xs font-bold uppercase tracking-wider text-[#1a3c5e] mb-2">Quick Actions</div>
            <div className="flex flex-wrap gap-2">
              <button
                data-testid="lead-schedule-followups"
                onClick={async () => {
                  try {
                    const { data } = await api.post("/sales/follow-ups/schedule", { lead_id: lead.id });
                    toast.success(`${data.count} follow-ups scheduled`);
                  } catch { toast.error("Failed"); }
                }}
                className="px-3 py-1.5 bg-blue-100 text-blue-700 hover:bg-blue-200 text-xs font-bold rounded-lg">
                Schedule Follow-Ups
              </button>
              <a
                data-testid="lead-create-quotation"
                href={`/admin/quotations?lead_id=${lead.id}&name=${encodeURIComponent(full.name || "")}&phone=${encodeURIComponent(full.phone || "")}&email=${encodeURIComponent(full.email || "")}&city=${encodeURIComponent(full.city || "")}&package=${encodeURIComponent(full.package_interest || "")}`}
                className="px-3 py-1.5 bg-[#F7941D] text-white hover:bg-[#e0861a] text-xs font-bold rounded-lg">
                Create Quotation
              </a>
            </div>
          </div>

          <div className="pt-3 border-t">
            <div className="text-xs font-bold uppercase tracking-wider text-[#1a3c5e] mb-1">Add Note</div>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} data-testid="lead-note-input" className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-[#F7941D]" placeholder="Call summary, key info..." />
            <button onClick={addNote} data-testid="lead-add-note" className="mt-1.5 px-3 py-1.5 bg-[#F7941D] text-white text-xs font-bold rounded-lg">Save note</button>
          </div>

          <div className="pt-3 border-t">
            <div className="text-xs font-bold uppercase tracking-wider text-[#1a3c5e] mb-2 flex items-center gap-1"><Calendar className="w-3 h-3" /> Activity Timeline</div>
            <div className="space-y-1.5">
              {(full.timeline || []).map((t, i) => (
                <div key={t.id || i} className="text-[11px] bg-gray-50 rounded-lg px-2 py-1.5">
                  <div className="font-semibold text-[#1a3c5e]">{(t.kind || t.event_type || t.event || "event").replace(/_/g, " ")}</div>
                  <div className="text-gray-500">{new Date(t.created_at).toLocaleString()}</div>
                  {t.detail && Object.keys(t.detail).length > 0 && (
                    <div className="text-gray-600 mt-0.5 font-mono">{JSON.stringify(t.detail).slice(0, 200)}</div>
                  )}
                </div>
              ))}
              {(!full.timeline || full.timeline.length === 0) && <div className="text-gray-400 italic text-[11px]">No activity yet</div>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function LeadModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "", city: "", plot_size: "", budget: "", requirement: "", package_interest: "", source: "manual", notes: "" });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!form.name || !form.phone) { toast.error("Name + phone required"); return; }
    setSaving(true);
    try {
      const r = await api.post("/leads", form);
      if (r.data?.existing) toast.message("Lead exists for this phone — opening instead");
      else toast.success(`${r.data.lead_id} created`);
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setSaving(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-5 w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-3"><h3 className="font-display text-lg font-bold text-[#1a3c5e]">New Lead</h3><button onClick={onClose}><X className="w-5 h-5" /></button></div>
        <div className="space-y-2">
          {[["name","Name*"],["phone","Phone*"],["email","Email"],["city","City"],["plot_size","Plot size"],["budget","Budget"],["requirement","Requirement"],["package_interest","Package (basic/premium/luxury)"],["notes","Notes"]].map(([k, lbl]) => (
            <div key={k}>
              <label className="text-[10px] font-bold uppercase text-gray-500">{lbl}</label>
              <input data-testid={`new-lead-${k}`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#F7941D]" />
            </div>
          ))}
          <div>
            <label className="text-[10px] font-bold uppercase text-gray-500">Source</label>
            <select value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm">
              {["manual","whatsapp","website","contact_form","meta","facebook","instagram","referral","landing"].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>
        <button onClick={submit} disabled={saving} data-testid="save-lead" className="mt-4 w-full px-3 py-2 bg-[#F7941D] hover:bg-[#e0861a] text-white font-bold rounded-lg disabled:opacity-50">{saving ? "Saving..." : "Create Lead"}</button>
      </div>
    </div>
  );
}

function Field({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="w-3.5 h-3.5 text-gray-400 mt-1" />
      <div className="flex-1">
        <div className="text-[10px] uppercase tracking-wider text-gray-500">{label}</div>
        <div className="text-sm text-[#1a3c5e] font-semibold break-all">{value || "—"}</div>
      </div>
    </div>
  );
}
